import {
  BadgeCheck,
  Calculator,
  Check,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Globe2,
  Languages,
  MapPinned,
  MousePointer2,
  PackageOpen,
  Plane,
  RefreshCcw,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UsersRound,
} from "lucide-react";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const STEP_TIMINGS = [500, 2850, 5200, 7550, 9900];
const LAST_STEP = 4;

const exportSteps = [
  {
    label: "Peta Pasar",
    title: "Cari pasar yang paling menjanjikan.",
    text: "Lihat negara prioritas, skor peluang, dan arah pasar yang paling cocok dengan produk UMKM.",
    icon: MapPinned,
  },
  {
    label: "Kesiapan",
    title: "Tahu apa yang harus dibenahi.",
    text: "Checklist membantu UMKM menyiapkan katalog, kapasitas, MOQ, kemasan, dan dokumen penting.",
    icon: ClipboardCheck,
  },
  {
    label: "Buyer",
    title: "Temukan buyer yang lebih relevan.",
    text: "Buyer workspace merangkum kecocokan, kebutuhan, dan langkah pendekatan yang bisa segera dilakukan.",
    icon: UsersRound,
  },
  {
    label: "Biaya",
    title: "Hitung harga sebelum kirim penawaran.",
    text: "Simulasikan freight, asuransi, duty, dan biaya per unit agar penawaran lebih percaya diri.",
    icon: Calculator,
  },
  {
    label: "Katalog",
    title: "Tampilkan profil ekspor yang siap dibagikan.",
    text: "Susun katalog dua bahasa dengan MOQ, kapasitas, lead time, sertifikasi, dan tombol inquiry.",
    icon: FileText,
  },
];

const sidebarItems = [
  { label: "Peta Pasar", icon: MapPinned },
  { label: "Kesiapan", icon: ClipboardCheck },
  { label: "Buyer", icon: UsersRound },
  { label: "Biaya", icon: Calculator },
  { label: "Katalog", icon: FileText },
];

const markets = [
  { name: "Singapura", score: 91, x: 455, y: 169, radius: 18, active: true },
  { name: "Jepang", score: 84, x: 520, y: 105, radius: 15 },
  { name: "Australia", score: 82, x: 502, y: 224, radius: 14 },
  { name: "UEA", score: 78, x: 356, y: 142, radius: 12 },
  { name: "Belanda", score: 75, x: 291, y: 80, radius: 11 },
];

function MarketRadarScreen() {
  return (
    <div className="export-demo-view">
      <div className="export-demo-view-head">
        <div><small>Opportunity Radar</small><h4>Peta pasar prioritas</h4></div>
        <span><Globe2 size={11} /> Kerajinan · HS 4602</span>
      </div>

      <div className="export-demo-stats">
        <div><span>Pasar teratas</span><strong>Singapura</strong><small><TrendingUp size={9} /> skor 91/100</small></div>
        <div><span>Buyer potensial</span><strong>24</strong><small>8 kecocokan tinggi</small></div>
        <div><span>Momentum</span><strong>Naik</strong><small>permintaan indikatif</small></div>
      </div>

      <div className="export-demo-market-layout">
        <article className="export-demo-map-card">
          <div className="export-demo-map-toolbar"><strong>Peluang berdasarkan negara</strong><span>Semakin besar lingkaran, semakin tinggi skor</span></div>
          <svg className="export-demo-world-map" viewBox="0 0 600 280" role="img" aria-label="Peta pasar ekspor prioritas dengan penanda negara">
            <defs>
              <linearGradient id="exportMapGradient" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#dcfce7" />
                <stop offset="100%" stopColor="#bbf7d0" />
              </linearGradient>
              <filter id="exportMarkerGlow" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <rect width="600" height="280" rx="18" fill="#f8fbf9" />
            <path d="M35 84C62 44 111 32 159 46c26 8 37 28 29 46-8 18-35 22-46 39-13 21-9 55-39 61-31 7-43-22-55-44-13-24-29-39-13-64Z" fill="url(#exportMapGradient)" />
            <path d="M172 171c22-18 57-21 75-3 13 13 7 31-4 44-9 12-11 29-30 31-22 2-35-13-45-29-9-16-11-30 4-43Z" fill="#d9fbe5" />
            <path d="M262 65c31-31 94-37 142-18 25 10 44 29 42 50-2 19-21 28-38 34-24 8-35 26-60 33-31 9-69 4-87-17-18-21-21-55 1-82Z" fill="url(#exportMapGradient)" />
            <path d="M387 153c19-15 52-19 81-7 19 8 30 21 25 36-7 19-35 23-53 22-23-2-51-10-61-27-6-10-3-17 8-24Z" fill="#d9fbe5" />
            <path d="M456 203c20-10 50-9 72 3 17 10 25 28 14 42-12 16-44 16-65 8-20-7-37-23-32-38 2-7 5-11 11-15Z" fill="#d9fbe5" />
            <path className="export-demo-route" d="M448 177 C431 158 410 151 389 147 C363 143 333 125 302 91" fill="none" stroke="#f97316" strokeDasharray="7 7" strokeLinecap="round" strokeWidth="3" />
            <circle cx="448" cy="177" r="6" fill="#0d944d" stroke="white" strokeWidth="3" />
            <text x="433" y="194" fill="#166534" fontSize="10" fontWeight="800">Indonesia</text>
            {markets.map((market) => (
              <g key={market.name} className={market.active ? "export-demo-marker is-active" : "export-demo-marker"}>
                {market.active && <circle cx={market.x} cy={market.y} r={market.radius + 9} fill="none" stroke="#f97316" strokeOpacity=".24" strokeWidth="5" />}
                <circle cx={market.x} cy={market.y} r={market.radius} fill={market.active ? "#f97316" : "#0d944d"} fillOpacity={market.active ? ".96" : ".84"} stroke="white" strokeWidth="3" filter={market.active ? "url(#exportMarkerGlow)" : undefined} />
                <text x={market.x} y={market.y + 3} textAnchor="middle" fill="white" fontSize="9" fontWeight="900">{market.score}</text>
              </g>
            ))}
          </svg>
        </article>

        <article className="export-demo-market-list">
          <div className="export-demo-panel-title"><div><small>Rekomendasi utama</small><strong>Pasar terbaik hari ini</strong></div><Target size={15} /></div>
          {[
            ["1", "Singapura", "91", "Distributor premium"],
            ["2", "Jepang", "84", "Specialty retail"],
            ["3", "Australia", "82", "Ethical lifestyle"],
          ].map((item, index) => (
            <div key={item[1]} className={index === 0 ? "export-demo-market-row is-selected" : "export-demo-market-row"}>
              <i>{item[0]}</i><div><strong>{item[1]}</strong><small>{item[3]}</small></div><b>{item[2]}</b>
            </div>
          ))}
          <div className="export-demo-market-tip"><Sparkles size={12} /><span>Mulai dari katalog ringan dan MOQ rendah untuk menguji respons buyer.</span></div>
        </article>
      </div>
    </div>
  );
}

function ReadinessScreen() {
  const checks = [
    ["Profil bilingual", true],
    ["MOQ & kapasitas", true],
    ["Lead time", true],
    ["Kemasan ekspor", true],
    ["Dokumen pendukung", false],
    ["Harga FOB", false],
  ] as const;

  return (
    <div className="export-demo-view">
      <div className="export-demo-view-head"><div><small>Export Readiness</small><h4>Skor kesiapan produk</h4></div><span><ShieldCheck size={11} /> Prioritas tindakan</span></div>
      <div className="export-demo-readiness-layout">
        <article className="export-demo-score-card">
          <div className="export-demo-score-ring"><span><strong>72</strong><small>/100</small></span></div>
          <p>Fondasi sudah baik</p>
          <small>Lengkapi dua hal penting sebelum menghubungi buyer.</small>
          <div className="export-demo-score-tags"><span>4 selesai</span><span>2 perlu aksi</span></div>
        </article>
        <article className="export-demo-checklist-card">
          <div className="export-demo-panel-title"><div><small>Checklist</small><strong>Yang sudah dan belum siap</strong></div><ClipboardCheck size={15} /></div>
          <div className="export-demo-check-grid">
            {checks.map(([label, complete]) => (
              <div key={label} className={complete ? "is-complete" : ""}><span>{complete ? <Check size={11} strokeWidth={3} /> : "!"}</span><strong>{label}</strong><small>{complete ? "Siap" : "Lengkapi"}</small></div>
            ))}
          </div>
        </article>
        <article className="export-demo-action-card">
          <span><Sparkles size={13} /></span>
          <div><small>Langkah berikutnya</small><strong>Siapkan harga FOB dan dokumen produk.</strong><p>CSJW membantu menyusun format informasi yang mudah dipahami buyer.</p></div>
          <b>+12 poin</b>
        </article>
      </div>
    </div>
  );
}

function BuyerScreen() {
  const buyers = [
    ["Nusantara Trading SG", "Singapura", "92", "Distributor"],
    ["Kura Lifestyle Co.", "Jepang", "86", "Specialty retail"],
    ["Terra Ethical Goods", "Australia", "81", "Lifestyle store"],
  ];

  return (
    <div className="export-demo-view">
      <div className="export-demo-view-head"><div><small>Buyer Workspace</small><h4>Buyer yang paling relevan</h4></div><span><UsersRound size={11} /> 24 prospek</span></div>
      <div className="export-demo-buyers-layout">
        <article className="export-demo-buyer-list">
          <div className="export-demo-panel-title"><div><small>Match score</small><strong>Prospek prioritas</strong></div><Target size={15} /></div>
          {buyers.map((buyer, index) => (
            <div key={buyer[0]} className={index === 0 ? "export-demo-buyer-row is-selected" : "export-demo-buyer-row"}>
              <span>{buyer[0].slice(0, 2).toUpperCase()}</span><div><strong>{buyer[0]}</strong><small>{buyer[1]} · {buyer[3]}</small></div><b>{buyer[2]}%</b>
            </div>
          ))}
        </article>
        <article className="export-demo-pitch-card">
          <div className="export-demo-pitch-head"><div><small>Buyer Pitch Copilot</small><strong>Draft perkenalan siap kirim</strong></div><Languages size={15} /></div>
          <div className="export-demo-email-meta"><span>To: hello@nusantaratrading.sg</span><span>EN</span></div>
          <p><b>Subject:</b> Handwoven Indonesian baskets for premium retail</p>
          <div className="export-demo-email-copy">Hello Nusantara Trading team,<br /><br />We produce handwoven natural-fiber baskets with flexible MOQ, private-label packaging, and a 21-day lead time…</div>
          <button type="button"><Send size={11} /> Pitch siap disalin</button>
        </article>
      </div>
      <div className="export-demo-pipeline">
        {[["Ditemukan", "24"], ["Dihubungi", "8"], ["Balasan", "3"], ["Negosiasi", "1"]].map(([label, value], index) => (
          <div key={label} className={index === 2 ? "is-active" : ""}><small>{label}</small><strong>{value}</strong><span style={{ width: `${[100, 68, 42, 18][index]}%` }} /></div>
        ))}
      </div>
    </div>
  );
}

function CostScreen() {
  const costs = [
    ["Nilai barang", "$1,200", 58],
    ["Freight", "$420", 25],
    ["Asuransi", "$36", 8],
    ["Duty & biaya lain", "$184", 14],
  ];

  return (
    <div className="export-demo-view">
      <div className="export-demo-view-head"><div><small>Landed Cost</small><h4>Simulasi biaya ekspor</h4></div><span><Calculator size={11} /> USD · FOB</span></div>
      <div className="export-demo-cost-layout">
        <article className="export-demo-cost-form">
          <div className="export-demo-panel-title"><div><small>Simulasi awal</small><strong>Pengiriman ke Singapura</strong></div><Plane size={15} /></div>
          <div className="export-demo-form-grid">
            <label>Jumlah unit<strong>120</strong></label>
            <label>Harga per unit<strong>$10</strong></label>
            <label>Freight<strong>$420</strong></label>
            <label>Duty estimasi<strong>8%</strong></label>
          </div>
          <button type="button"><Calculator size={11} /> Hitung ulang</button>
        </article>
        <article className="export-demo-cost-result">
          <small>Estimasi landed total</small><strong>$1,840</strong><p>atau <b>$15.33</b> per unit</p>
          <div className="export-demo-cost-bars">
            {costs.map(([label, value, width]) => (
              <div key={label as string}><span><i>{label as string}</i><b>{value as string}</b></span><em><u style={{ width: `${width}%` }} /></em></div>
            ))}
          </div>
          <div className="export-demo-margin-note"><TrendingUp size={12} /><span>Target retail $28 memberi ruang margin indikatif 45%.</span></div>
        </article>
      </div>
    </div>
  );
}

function CatalogScreen() {
  return (
    <div className="export-demo-view export-demo-view--catalog">
      <div className="export-demo-view-head"><div><small>Export Catalog</small><h4>Profil produk siap dibagikan</h4></div><span><Languages size={11} /> Indonesia / English</span></div>
      <div className="export-demo-catalog-layout">
        <aside className="export-demo-catalog-form">
          <div className="export-demo-panel-title"><div><small>Informasi utama</small><strong>Lengkap dan konsisten</strong></div><BadgeCheck size={15} /></div>
          {[
            ["MOQ", "120 units"],
            ["Capacity", "2,500 / month"],
            ["Lead time", "21 days"],
            ["Packaging", "Recycled carton"],
            ["Private label", "Available"],
          ].map(([label, value]) => <label key={label}><span>{label}</span><strong>{value}</strong><CheckCircle2 size={11} /></label>)}
          <button type="button"><CheckCircle2 size={11} /> Katalog tersimpan</button>
        </aside>
        <article className="export-demo-catalog-preview">
          <div className="export-demo-catalog-brand"><span><PackageOpen size={14} /></span><div><strong>Ruang Anyam</strong><small>Handcrafted in Indonesia</small></div><b>EN</b></div>
          <div className="export-demo-catalog-hero">
            <div><small>Natural fiber collection</small><h5>Handwoven pieces for thoughtful spaces.</h5><p>Made by Indonesian artisans with responsible materials and export-ready packaging.</p><button type="button">Request quotation</button></div>
            <span><PackageOpen size={30} /><strong>Woven Basket</strong><small>Natural rattan</small></span>
          </div>
          <div className="export-demo-catalog-specs"><span><b>120</b>MOQ</span><span><b>2.5K</b>Monthly capacity</span><span><b>21d</b>Lead time</span><span><b>FOB</b>Incoterm</span></div>
          <div className="export-demo-catalog-footer"><span><ShieldCheck size={10} /> Quality checked</span><span><Globe2 size={10} /> Worldwide inquiry</span></div>
        </article>
      </div>
    </div>
  );
}

function ExportScreen({ step }: { step: number }) {
  const screens = [<MarketRadarScreen />, <ReadinessScreen />, <BuyerScreen />, <CostScreen />, <CatalogScreen />];
  return <div key={`export-screen-${step}`} className="export-demo-screen-enter">{screens[step]}</div>;
}

function cursorPosition(step: number) {
  const positions = [
    [70, 50],
    [44, 63],
    [60, 46],
    [71, 60],
    [80, 73],
  ];
  return positions[step] ?? positions[0];
}

export function ExportDemoSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const visibilityRef = useRef(false);
  const [inView, setInView] = useState(false);
  const [runId, setRunId] = useState(0);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        if (visible && !visibilityRef.current) {
          visibilityRef.current = true;
          setInView(true);
          setRunId((current) => current + 1);
        } else if (!entry.isIntersecting || entry.intersectionRatio < 0.05) {
          visibilityRef.current = false;
          setInView(false);
          setStep(0);
        }
      },
      { threshold: [0, 0.05, 0.2, 0.45, 0.75], rootMargin: "-4% 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setStep(0);
    if (reducedMotion) {
      setStep(LAST_STEP);
      return;
    }
    const timers = STEP_TIMINGS.map((delay, index) => window.setTimeout(() => setStep(index), delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [inView, runId]);

  const activeStep = exportSteps[step];
  const cursor = cursorPosition(step);
  const progress = Math.round(((step + 1) / exportSteps.length) * 100);

  const replay = () => {
    setStep(0);
    setRunId((current) => current + 1);
  };

  return (
    <section ref={sectionRef} id="demo-export" className="export-demo-section relative overflow-hidden py-20 sm:py-24">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,#fff_0%,#f5fbf7_46%,#fff8f1_100%)]" />
      <div className="soft-grid pointer-events-none absolute inset-0 opacity-35" />
      <div className="pointer-events-none absolute -left-40 top-16 h-96 w-96 rounded-full bg-brand-200/35 blur-3xl" />
      <div className="pointer-events-none absolute -right-44 bottom-4 h-[28rem] w-[28rem] rounded-full bg-accent-200/30 blur-3xl" />

      <div className="app-container relative">
        <div className={`scroll-reveal scroll-reveal--up ${inView ? "is-visible" : ""} text-center`}>
          <span className="eyebrow"><Plane size={14} /> Demo Siap Export</span>
          <h2 className="section-title mx-auto mt-5 max-w-4xl">Dari produk lokal, ke peluang global.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-500">
            Satu dashboard untuk membaca peluang pasar, mengecek kesiapan, menemukan buyer, menghitung biaya, dan menyiapkan katalog ekspor.
          </p>
        </div>

        <div className="export-demo-stage mt-12">
          <div className="export-demo-copy">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-black text-brand-100"><Sparkles size={14} /> Simulasi dashboard ekspor</span>
            <div key={`export-copy-${runId}-${step}`} className="export-demo-copy-enter">
              <p className="mt-5 text-xs font-black uppercase tracking-[.17em] text-accent-300">{activeStep.label}</p>
              <h3 className="mt-2 max-w-xl text-3xl font-black tracking-[-.045em] text-white sm:text-4xl">{activeStep.title}</h3>
              <p className="mt-4 max-w-xl text-sm leading-7 text-white/65 sm:text-base">{activeStep.text}</p>
            </div>

            <div className="mt-7 space-y-2.5">
              {exportSteps.map(({ label, icon: Icon }, index) => (
                <button key={label} type="button" onClick={() => setStep(index)} className={`export-demo-step ${step === index ? "is-active" : ""} ${step > index ? "is-complete" : ""}`}>
                  <span>{step > index ? <Check size={14} strokeWidth={3} /> : <Icon size={16} />}</span><strong>{label}</strong><i>{index + 1}</i>
                </button>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button type="button" onClick={replay} className="export-demo-replay"><RefreshCcw size={17} /> Putar ulang demo</button>
              <Link to="/siap-export" target="_blank" rel="noopener noreferrer" className="export-demo-detail">Lihat Siap Export <ExternalLink size={16} /></Link>
            </div>
          </div>

          <div className="export-demo-device-wrap" aria-live="polite">
            <div className="dashboard-demo-laptop export-demo-laptop" key={`export-dashboard-${runId}`}>
              <div className="dashboard-demo-laptop-lid">
                <div className="dashboard-demo-camera" />
                <div className="export-demo-app">
                  <aside className="export-demo-sidebar">
                    <div className="export-demo-brand"><img src="/brand/logo-mark-exact.png" alt="" /><div><b>CSJW</b><small>Siap Export</small></div></div>
                    <nav>{sidebarItems.map(({ label, icon: Icon }, index) => <span key={label} className={step === index ? "is-active" : ""}><Icon size={13} />{label}</span>)}</nav>
                    <div className="export-demo-plan"><small>Paket aktif</small><strong>Siap Export</strong><span><Globe2 size={10} /> Global toolkit</span></div>
                  </aside>
                  <main className="export-demo-main"><ExportScreen step={step} /></main>
                  <span className="export-demo-cursor" style={{ "--cursor-x": `${cursor[0]}%`, "--cursor-y": `${cursor[1]}%` } as CSSProperties} aria-hidden="true"><MousePointer2 size={19} fill="white" /></span>
                  {step === 0 && <div className="export-demo-toast"><span><Target size={14} /></span><div><strong>Pasar prioritas ditemukan</strong><small>Singapura · Opportunity 91</small></div><CheckCircle2 size={14} /></div>}
                  {step === 1 && <div className="export-demo-toast"><span><ClipboardCheck size={14} /></span><div><strong>Skor kesiapan diperbarui</strong><small>4 dari 6 elemen sudah siap</small></div><CheckCircle2 size={14} /></div>}
                  {step === 2 && <div className="export-demo-toast"><span><UsersRound size={14} /></span><div><strong>Buyer match tinggi</strong><small>Nusantara Trading SG · 92%</small></div><CheckCircle2 size={14} /></div>}
                  {step === 3 && <div className="export-demo-toast"><span><Calculator size={14} /></span><div><strong>Landed cost selesai</strong><small>$15.33 per unit</small></div><CheckCircle2 size={14} /></div>}
                  {step === 4 && <div className="export-demo-toast"><span><FileText size={14} /></span><div><strong>Katalog siap dibagikan</strong><small>Versi Indonesia dan English</small></div><CheckCircle2 size={14} /></div>}
                </div>
              </div>
              <div className="dashboard-demo-laptop-base"><span /></div>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-4xl">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[.14em] text-slate-400"><span>{activeStep.label}</span><span>{progress}%</span></div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white shadow-inner"><span className="block h-full rounded-full bg-gradient-to-r from-brand-500 via-accent-500 to-brand-600 transition-[width] duration-700 ease-out" style={{ width: `${progress}%` }} /></div>
        </div>
      </div>
    </section>
  );
}
